import { useLayoutEffect } from 'react';

/**
 * How the header gives way where its sides have no room beside the bar (Figma's narrow-window rule): «Поделиться» drops
 * its label, the sides close up to 12 px, the chat button goes into the people's list, their avatars come down to your own, then the bar's own
 * labels go («Проекты», «ИИ-агент», photo · gallery · «Ещё»), the 2D/3D switch narrows, and last the floor's name.
 */
export const SQUEEZE_STEPS = ['share', 'tight', 'chat', 'avatars', 'bar', 'switch', 'floor'] as const;
const PEOPLE_STEPS = [SQUEEZE_STEPS.indexOf('chat'), SQUEEZE_STEPS.indexOf('avatars')];

/** The first number of steps after which neither side runs into the bar; `people` false skips the two steps of the people. */
export function squeezeLevel(overflows: (level: number) => { right: boolean; left: boolean }, people: boolean): number {
  for (let level = 0; level < SQUEEZE_STEPS.length; level++) {
    if (!people && PEOPLE_STEPS.includes(level - 1)) continue;
    const { right, left } = overflows(level);
    if (!right && !left) return level;
  }
  return SQUEEZE_STEPS.length;
}

/** Its content's width, past a `max-width` that clamps the box to its column (the AI agent's launcher). */
const widthOf = (element: Element) => Math.max((element as HTMLElement).offsetWidth, element.scrollWidth);

/** Does a side's content, as the people want it at this step, run past its column? */
function overflowsAt(root: HTMLElement, level: number): { right: boolean; left: boolean } {
  const end = root.querySelector<HTMLElement>('.top-end'), start = root.querySelector<HTMLElement>('.top-start');
  const side = (column: HTMLElement | null) => {
    if (!column) return false;
    const gap = parseFloat(getComputedStyle(column).columnGap) || 0;
    const widths = [...column.children].map(child => child.classList.contains('collab-presence')
      ? Number(child.getAttribute(level > SQUEEZE_STEPS.indexOf('avatars') ? 'data-want-min' : 'data-want')) || 0 : widthOf(child)).filter(width => width > 0);
    return widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1) > column.clientWidth + .5;
  };
  return { right: side(end), left: side(start) };
}

/**
 * Keeps the header's steps (`squeeze-<step>` classes on `.topbar`) in step with the room it measures: on a resize of the
 * header or its bar, and when the people's wanted width changes. Laid out a frame later, so its own changes do not loop.
 */
export function useHeaderSqueeze(root: HTMLElement | null): void {
  useLayoutEffect(() => {
    if (!root) return;
    let frame = 0;
    const apply = (level: number) => { for (const [index, step] of SQUEEZE_STEPS.entries()) root.classList.toggle('squeeze-' + step, index < level); };
    const run = () => {
      frame = 0;
      const people = Number(root.querySelector('.collab-presence')?.getAttribute('data-people') ?? 0) > 0;
      const chat = root.querySelector<HTMLElement>('.chat-button'), focused = !!chat && document.activeElement === chat;
      const level = squeezeLevel(level => { apply(level); return overflowsAt(root, level); }, people);
      apply(level);
      root.dataset.squeeze = String(level);
      if (focused && !chat!.offsetWidth) root.querySelector<HTMLElement>('.collab-more')?.focus({ preventScroll: true });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(run); };
    run();
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    const bar = root.querySelector('.top-bar');
    if (bar) resize.observe(bar);
    const mutation = new MutationObserver(schedule);
    mutation.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-want', 'data-want-min', 'data-people'] });
    return () => { resize.disconnect(); mutation.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [root]);
}
